import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Table, Badge, Button, OverlayTrigger, Popover, Modal } from "react-bootstrap";
import "../../css/OrderProducts.css";

const SaladsTable = ({
  order,
  showPopinCreditInvoiceItem,
  showPopinRefundInvoiceItem,
  showPopinRefundSalad,
  showPopinCreditSalad,
  readOnly,
  refundDisabled,
  onRestartSaladPrep,
}) => {
  const salads = order.dynamic_products || [];

  // State pour le modal de repréparation
  const [showReprepModal, setShowReprepModal] = useState(false);
  const [selectedSalad, setSelectedSalad] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  const handleSaladLevelCredit = function(salad) {
    showPopinCreditSalad(salad, order);
  };

  const handleSaladLevelRefund = function(salad) {
    showPopinRefundSalad(salad, order);
  };

  // Handler pour ouvrir le modal de repréparation
  const handleRestartSaladPrep = function(salad, task) {
    setSelectedSalad(salad);
    setSelectedTask(task);
    setShowReprepModal(true);
  };

  // Handler pour confirmer la repréparation
  const handleConfirmReprep = function() {
    if (onRestartSaladPrep && selectedTask) {
      onRestartSaladPrep(order.id, selectedTask.choiceset_task_id);
      setShowReprepModal(false);
    }
  };

  // Méthode pour rendre une ligne d'ingrédient (order_product imbriqué)
  const renderIngredientRow = (item, saladQuantity) => {
    const quantity = item.quantity || 0;
    const quantityRecredit = item.quantity_recredit || 0;
    const quantityRefund = item.quantity_refund || 0;

    // Format quantité avec total si salade quantité > 1
    let quantityDisplay = quantity;
    if (saladQuantity > 1) {
      const totalQuantity = quantity * saladQuantity;
      quantityDisplay = quantity + "(par salade, " + totalQuantity + "total)";
    }

    return (
      <tr key={item.order_product_id} className="ingredient-row">
        <td></td>
        <td></td>
        <td>
          <b>{quantityDisplay}</b>
        </td>
        <td>
          <a
            href={process.env.REACT_APP_PRODUCTS + "/products/" + item.product_id}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.selling_name}
          </a>
          &nbsp;
          {quantityRecredit > 0 && (
            <Badge variant="default">
              recredit: {quantityRecredit}
            </Badge>
          )}
          {quantityRefund > 0 && (
            <Badge variant="default">
              remboursé: {quantityRefund}
            </Badge>
          )}
        </td>
        <td colSpan={4}></td>
      </tr>
    );
  };

  // Méthode pour rendre un extra invoice item avec badge "Extra"
  const renderExtraInvoiceItem = (invoiceItem, saladQuantity) => {
    const totalRefunded = invoiceItem.quantity_refunded || 0;
    const canRefund = invoiceItem.quantity_bought > totalRefunded;
    const quantityRemaining = invoiceItem.quantity_bought - totalRefunded;

    return (
      <React.Fragment key={invoiceItem.invoice_item_id}>
        <tr className="extra-invoice-item">
          <td></td>
          <th colSpan={2}>
            <strong>{invoiceItem.quantity_bought}</strong>
            {totalRefunded > 0 && (
              <small className="text-muted d-block">
                (Remb: {totalRefunded})
              </small>
            )}
          </th>
          <th colSpan={3}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>
                  <a
                    href={process.env.REACT_APP_PRODUCTS + "/products/" + invoiceItem.product_id}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {invoiceItem.selling_name}
                  </a>
                </strong>
                <Badge variant="warning" className="ml-2">
                  Extra
                </Badge>
                <small className="text-muted ml-2">
                  ({invoiceItem.total_price && invoiceItem.total_price.toFixed(2)}€)
                </small>
              </div>
            </div>
          </th>
          <td>
            <Button
              variant="primary"
              size="xs"
              className="mr-1"
              onClick={() => showPopinCreditInvoiceItem(invoiceItem, order)}
              disabled={readOnly || quantityRemaining === 0}
              title={quantityRemaining === 0 ? "Tout déjà remboursé/recrédité" : ""}
            >
              Recrédit
            </Button>
          </td>
          <td>
            <Button
              variant="warning"
              size="xs"
              onClick={() => showPopinRefundInvoiceItem(invoiceItem, order)}
              disabled={readOnly || refundDisabled || !canRefund}
              title={!canRefund ? "Tout déjà remboursé" : ""}
            >
              Remboursement
            </Button>
          </td>
        </tr>
      </React.Fragment>
    );
  };

  // Méthode pour rendre le base invoice item
  const renderBaseInvoiceItem = (invoiceItem, saladQuantity) => {
    const totalRefunded = invoiceItem.quantity_refunded || 0;
    const canRefund = invoiceItem.quantity_bought > totalRefunded;
    const quantityRemaining = invoiceItem.quantity_bought - totalRefunded;

    return (
      <React.Fragment key={invoiceItem.invoice_item_id}>
        <tr className="table-primary base-invoice-item">
          <td></td>
          <th colSpan={2}>
            <strong>{invoiceItem.quantity_bought}</strong>
            {totalRefunded > 0 && (
              <small className="text-muted d-block">
                (Remb: {totalRefunded})
              </small>
            )}
          </th>
          <th colSpan={3}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>
                  <a
                    href={process.env.REACT_APP_PRODUCTS + "/products/" + invoiceItem.product_id}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {invoiceItem.selling_name}
                  </a>
                </strong>
                <small className="text-muted ml-2">
                  ({invoiceItem.total_price && invoiceItem.total_price.toFixed(2)}€)
                </small>
              </div>
            </div>
          </th>
          <td>
            <Button
              variant="primary"
              size="xs"
              className="mr-1"
              onClick={() => showPopinCreditInvoiceItem(invoiceItem, order)}
              disabled={readOnly || quantityRemaining === 0}
              title={quantityRemaining === 0 ? "Tout déjà remboursé/recrédité" : ""}
            >
              Recrédit
            </Button>
          </td>
          <td>
            <Button
              variant="warning"
              size="xs"
              onClick={() => showPopinRefundInvoiceItem(invoiceItem, order)}
              disabled={readOnly || refundDisabled || !canRefund}
              title={!canRefund ? "Tout déjà remboursé" : ""}
            >
              Remboursement
            </Button>
          </td>
        </tr>
        {invoiceItem.order_products && invoiceItem.order_products.map((item) =>
          renderIngredientRow(item, saladQuantity)
        )}
      </React.Fragment>
    );
  };


  // Helper: Traduction des statuts
  const translateStatus = (status) => {
    switch (status) {
      case "done":
        return "préparée";
      case "prep":
        return "en préparation";
      case "to-prep":
        return "à préparer";
      default:
        return status;
    }
  };

  // Obtenir la variante du badge selon le statut
  const getVariant = (status) => {
    switch (status) {
      case "done":
        return "success";  // Vert
      case "prep":
        return "warning";  // Jaune/Orange
      case "to-prep":
        return "secondary";  // Gris
      default:
        return "secondary";
    }
  };

  // Rendu du badge de statut de préparation avec tooltip
  const renderPrepStatusBadge = (salad) => {
    // Gestion edge case: pas de prep_status (tâches pas encore créées)
    if (!salad.prep_status) {
      return null;  // Ne pas afficher de badge si pas de données de prep
    }

    const { total_quantity, aggregate_status } = salad.prep_status;
    
    // Phase 3: Affichage simplifié du badge (uniquement le statut)
    const statusLabel = translateStatus(aggregate_status);

    // Création du contenu du popover avec détails des tâches en tableau
    const popover = (
      <Popover
        id={`prep-status-popover-${salad.choiceset_id}`}
        style={{ maxWidth: "none", width: "500px" }}
      >
        <Popover.Content style={{width: "100%"}}>
          {salad.prep_tasks && salad.prep_tasks.length > 0 ? (
            <Table size="sm" responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Prép.</th>
                  <th>Code</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {salad.prep_tasks.map((task, index) => (
                  <tr key={task.stafftask_id}>
                    <td><small>#{task.stafftask_id}</small></td>
                    <td>{task.worker_name || ""}</td>
                    <td>{task.prep_code || ""} - {task.location || ""} </td>
                    <td>{translateStatus(task.status)}</td>
                    {task.reprep_stafftask_id
                      ? (<td>Reprep: #{task.reprep_stafftask_id}</td>)
                      : (
                        <td>
                          {task.status === 'done' && !readOnly && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRestartSaladPrep(salad, task);
                              }}
                            >
                              Repréparer
                            </Button>
                          )}
                        </td>
                      )

                    }
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>Aucune tâche de préparation trouvée.</p>
          )}
        </Popover.Content>
      </Popover>
    );

    // Wrapper du badge avec OverlayTrigger
    return (
      <OverlayTrigger trigger="click" placement="bottom" overlay={popover} rootClose>
        <Button variant={getVariant(aggregate_status)} size="xs" style={{ margin: "auto 1rem" }}>
          {statusLabel}
        </Button>
      </OverlayTrigger>
    );
  };

  const renderSaladHeader = (salad) => {
    // Calculate if everything in the salad is fully refunded
    const allInvoiceItems = [salad.base_invoice_item];
    if (salad.extra_invoice_items && salad.extra_invoice_items.length > 0) {
      salad.extra_invoice_items.forEach(item => allInvoiceItems.push(item));
    }

    const allFullyRefunded = allInvoiceItems.every(item => {
      const totalRefunded = item.quantity_refunded || 0;
      return item.quantity_bought <= totalRefunded;
    });

    // Si pas d'extra, il y a uniquement l'invoice item de base
    const hasExtra = allInvoiceItems.length > 1

    return (
      <tr key={salad.choiceset_id + "_header"} className="salad-header">
        <th colSpan={6}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>
                {salad.base_product_selling_name} (x{salad.total_quantity}) - {(salad.total_price || 0).toFixed(2)}€
              </strong>
              {renderPrepStatusBadge(salad)}
            </div>
          </div>
        </th>
        <td>
            <div>
              <Button
                variant="primary"
                size="xs"
                className="mr-2"
                onClick={() => handleSaladLevelCredit(salad)}
                disabled={readOnly || allFullyRefunded || !hasExtra}
                title={allFullyRefunded ? "Tout déjà remboursé/recrédité" : "Recréditer toute la salade (base + extras)"}
              >
                Recrédit
              </Button>
          </div>
        </td>
        <td>
          <div>
            <Button
              variant="warning"
              size="xs"
              onClick={() => handleSaladLevelRefund(salad)}
              disabled={readOnly || refundDisabled || allFullyRefunded || !hasExtra}
              title={allFullyRefunded ? "Tout déjà remboursé" : "Rembourser toute la salade (base + extras)"}
            >
              Remboursement
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  // Early return if no data
  if (!salads || salads.length === 0) {
    return null;
  }

  // Render using hierarchical structure (Salad → Base + Extras)
  const renderSalads = () => {
    return salads.map((salad) => (
      <React.Fragment key={salad.choiceset_id}>
        {renderSaladHeader(salad)}
        {renderBaseInvoiceItem(salad.base_invoice_item, salad.total_quantity)}
        {salad.extra_invoice_items && salad.extra_invoice_items.map((extraItem) =>
          renderExtraInvoiceItem(extraItem, salad.total_quantity)
        )}
      </React.Fragment>
    ));
  };



  const renderReprepModal = () => (
    <Modal show={showReprepModal} onHide={() => setShowReprepModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Repréparer la salade</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedSalad && selectedTask && (
          <div>
            <p>
              <strong>{selectedSalad.base_product_selling_name}</strong>
              {selectedSalad.prep_status && selectedSalad.prep_status.total_quantity > 1 && selectedTask && (
                <span> - Unité {selectedSalad.prep_tasks.findIndex(t => t.choiceset_task_id === selectedTask.choiceset_task_id) + 1}/{selectedSalad.prep_status.total_quantity}</span>
              )}
            </p>
            <Alert variant="warning">
              Cette action créera une nouvelle tâche de préparation. La tâche existante sera annulée.
            </Alert>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowReprepModal(false)}>
          Annuler
        </Button>
        <Button variant="danger" onClick={handleConfirmReprep}>
          Repréparer
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="mt-3">
      <Table size="sm" hover className="OrderProduct">
        <tbody>
          <tr>
            <th colSpan={6}>Salades</th>
          </tr>
          {renderSalads()}
        </tbody>
      </Table>

      {/* T15: Modal de repréparation */}
      {renderReprepModal()}
    </div>
  );
};

SaladsTable.propTypes = {
  order: PropTypes.object.isRequired,
  showPopinCreditInvoiceItem: PropTypes.func.isRequired,
  showPopinRefundInvoiceItem: PropTypes.func.isRequired,
  showPopinRefundSalad: PropTypes.func.isRequired,
  showPopinCreditSalad: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  refundDisabled: PropTypes.bool,
  onRestartSaladPrep: PropTypes.func, // T14: Handler pour la repréparation
};

export default SaladsTable;
